import styles from './deliveryOrderItemsTable.module.css'
import { useEffect, useMemo, useState } from "react"
import { Button, Column, FieldType, FieldValue, NumberField, O, Section, Table } from "../../../../../features/ui"
import { Item, Operation } from "../../../../operations"
import { Lot, Product, ProductName, ProductType, useLotLoaders, useProductLoaders } from "../../../../products"
import { isDefined } from '../../../../../utils/typeUtils'
import { DeliveryItemRow } from '../../types'
import { DeliveryMethod } from '../../../state/types'

type Props = {
    method: DeliveryMethod
    operation: Operation
    itemRows: DeliveryItemRow[]
    title?: string
    onChange: (items: DeliveryItemRow[]) => void
    onDefineLot?: (itemId: string) => () => void
}

export const DeliveryOrderItemsTable = (props: Props) => {
    const { method, operation, itemRows, title, onChange, onDefineLot } = props

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])
    const [rows, setRows] = useState<DeliveryItemRow[]>(itemRows)

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const load = async (items: Item[]) => {
        const { loadProducts } = productLoaders()
        const products = await loadProducts(items.map(item => item.productId))
        setProducts(products)
        const { loadProductsLots } = lotLoaders()
        const lots = await loadProductsLots(products.map(product => product.id))
        setLots(lots)
    }

    useEffect(() => {
        load(operation.items)
    }, [operation.id])

    useEffect(() => {
        onChange(rows)
    }, [rows])

    useEffect(() => {
        setRows(itemRows)
    }, [itemRows])

    const onChangeQuantity = (itemId: string) => (value?: FieldValue) => {
        const newQuantity = value as O<number>
        if (!isDefined(newQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            quantity: newQuantity!
        } : row))
    }

    // const handleDefineLot = (itemId: string) => () => {
    //     onDefineLot && onDefineLot(itemData)()
    // }

    const columns = useMemo(() => {
        const columnList: Column[] = [
            {
                name: 'itemId',
                type: FieldType.TEXT,
                label: 'Producto',
                render: (value?: FieldValue) => {
                    const row = rows.find(row => row.itemId === value!)!
                    const product = products.find(product => product.id === row.productId)
                    const lot = lots.find(lot => lot.id === row.lotId)
                    return <ProductName product={product} lot={lot} showSupplierCode />
                }
            },
            {
                name: 'pending',
                type: FieldType.NUMBER,
                label: 'Pendiente'
            },
            {
                name: 'quantity',
                type: FieldType.NUMBER,
                label: 'Cantidad',
                render: (value?: FieldValue) => {
                    const row = rows.find(row => row.itemId === value!)!
                    const product = products.find(product => product.id === row.productId)
                    return (
                        <NumberField
                            id="quantity"
                            value={row.quantity}
                            integer={product?.type !== ProductType.BULK}
                            min={0}
                            max={row.pending}
                            className={styles.qtyField}
                            onChange={onChangeQuantity(row.itemId)}
                        />
                    )
                }
            }
        ]
        
        if (method === DeliveryMethod.RECEPTION && onDefineLot) {
            const defineLotColumn: Column = {
                name: 'defineLot',
                type: FieldType.TEXT,
                label: '',
                render: (value?: FieldValue) => {
                    const row = rows.find(row => row.itemId === value!)!
                    const product = products.find(product => product.id === row.productId)
                    return product?.loted ? (
                        <Button
                            label="Definir Partida"
                            icon="grid"
                            kind="tertiary"
                            small
                            onClick={onDefineLot(row.itemId)}
                        />
                    ) : <></>
                }
            }
            columnList.push(defineLotColumn)
        }
        
        return columnList
    }, [method, rows, products, lots, onDefineLot])

    const tableRows = useMemo(() => rows.map(row => ({
        itemId: row.itemId,
        pending: row.pending,
        quantity: row.itemId,
        defineLot: row.itemId
    })), [rows])

    return (
        <Section
            id="delivery-order-items"
            title={title || "Productos"}
            space={8}
        >
            <Table
                columns={columns}
                rows={tableRows}
                rowId="itemId"
                readOnly
                alignRight={false}
            />
        </Section>
    )
}
